import styled from '@emotion/styled';
import { Button, Input, Modal, Select, Space, Table, notification } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { FaPen, FaTrash } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { ConnectedUserEntity } from '../../../../../entities/ConnectedUserEntity';
import { OperationEntity } from '../../../../../entities/operation.entity';
import { TitreEntity } from '../../../../../entities/titre.entity';
import { PRIMARY } from '../../../../../shared/colors';
import { formatNum } from '../../../../../shared/constants';
import { deleteOperation, updateOperation } from '../network/network';

const MAX_VISIBLE_ACTIVITY_ROWS = 6;
const BUY_COLOR = '#008f4f';
const SELL_COLOR = '#d92d20';
const EMPTY_VALUE = '--';

const Container = styled.div`
  color: var(--mt-text);
  padding: 8px 10px 10px;
  font-family: Poppins-R;
  height: 100%;
  max-height: none;
  overflow-x: auto;
  overflow-y: auto;

  th {
    background-color: ${PRIMARY} !important;
    color: white !important;
    font-family: Poppins-B;
    font-size: 11px;
    font-weight: normal;
    text-transform: uppercase;
  }

  .activity-summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 6px;
    color: var(--mt-muted);
    font-size: 11px;
  }

  .activity-summary strong {
    color: var(--mt-text);
    font-family: Poppins-B;
  }

  .activity-summary span {
    padding: 3px 8px;
    border: 1px solid rgba(255, 176, 0, 0.35);
    border-radius: 999px;
    color: var(--mt-warning);
    font-family: Poppins-B;
  }

  .side-pill,
  .status-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 54px;
    padding: 2px 7px;
    border-radius: 999px;
    font-family: Poppins-B;
    font-size: 10px;
    text-transform: uppercase;
  }

  .side-pill.buy {
    color: ${BUY_COLOR};
    background-color: rgba(0, 143, 79, 0.1);
  }

  .side-pill.sell {
    color: ${SELL_COLOR};
    background-color: rgba(217, 45, 32, 0.1);
  }

  .status-pill {
    color: var(--mt-warning);
    background-color: rgba(255, 176, 0, 0.12);
    border: 1px solid rgba(255, 176, 0, 0.25);
  }

  .action-button {
    width: 26px;
    height: 26px;
    border-radius: 999px;
    background-color: var(--mt-surface) !important;
    border: 1px solid var(--mt-border);
  }

  .empty-state {
    display: grid;
    min-height: 110px;
    place-items: center;
    color: var(--mt-muted);
    border: 1px dashed var(--mt-border);
    border-radius: 8px;
    background-color: var(--mt-surface);
  }

  &::-webkit-scrollbar {
    width: 0.5vw;
  }

  &::-webkit-scrollbar-thumb {
    background-image: linear-gradient(to bottom, #eceff1, #eceff1);
    border-radius: 10px;
  }
`;

export const Orders = ({
  orders,
  updateOders,
  getSelectedTitre,
}: {
  orders: any[];
  updateOders: () => void;
  getSelectedTitre: (titre: TitreEntity) => void;
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showModifModal, setShowModifModal] = useState(false);
  const [selectedTitre, setSelectedTitre] = useState<TitreEntity>();
  const [selectedOPE, setSelectedOPE] = useState<OperationEntity>();
  const [isLoading, setIsLoading] = useState(false);
  const [lmtOptions, setLmtOptions] = useState<any[]>([]);
  const [operation, setOperation] = useState({
    qte: 0,
    prix: 0,
    decote: 0,
  });
  const connectedUser: ConnectedUserEntity = useSelector(
    (state: any) => state.userReducer,
  ).user;
  interface PriceItem {
    value: number;
    label: string;
  }
  useEffect(() => {
    if (selectedTitre) {
      let prices: PriceItem[] = [];
      let percent = 0.85;
      let val = 0;
      while (percent <= 1.16) {
        val = Math.floor(selectedTitre.valeurNominale * percent);
        prices.push({
          value: val,
          label: `${Math.floor(percent * 100)}% - ${formatNum(val)} XAF`,
        });
        percent += 0.01;
      }

      setLmtOptions(prices);
    }
  }, [selectedTitre]);

  const deleteOffer = () => {
    setIsLoading(true);
    if (selectedOPE) {
      deleteOperation(connectedUser.id, selectedOPE.id).then(
        ({ statusCode, data }) => {
          if (statusCode?.toString()[0] === '2') {
            notification.success({
              message: 'Offre supprimé',
              description: 'Votre ordre a été supprimé avec succès',
            });
            updateOders();
          } else {
            notification.info({
              message: 'Erreur',
              description: data.message,
            });
          }
          setShowDeleteModal(false);
          setIsLoading(false);
        },
      );
    }
  };

  const modifOffer = () => {
    setIsLoading(true);
    if (selectedOPE) {
      updateOperation(connectedUser.id, selectedOPE.id, operation).then(
        ({ statusCode, data }) => {
          if (statusCode.toString()[0] === '2') {
            notification.success({
              message: 'Offre mise à jour',
              description: 'Votre ordre a été mise à jour avec succès',
            });
            // afficher le statuts des matchs
            notification.info({
              message: "Statut de l'ordre",
              description: data.match.message,
            });
            updateOders();
          } else {
            notification.error({
              message: 'Erreur',
              description: data.message,
            });
          }
          setIsLoading(false);
          setShowModifModal(false);
        },
      );
    }
  };

  const COLUMNS = [
    {
      title: 'Pays',
      render: (cell: any, row: OperationEntity) =>
        row.titre?.pays || EMPTY_VALUE,
    },
    {
      title: 'CODE',
      render: (cell: any, row: OperationEntity) =>
        row.titre?.code || EMPTY_VALUE,
    },
    {
      title: 'Detenteur',
      render: (cell: any, row: OperationEntity) =>
        row.detenteur?.code_referent || EMPTY_VALUE,
    },
    {
      title: 'Opération',
      dataIndex: 'typeOperation',
      render: (cell: string) => (
        <span className={`side-pill ${cell === 'ACHAT' ? 'buy' : 'sell'}`}>
          {cell}
        </span>
      ),
    },
    {
      title: 'Date Ope',
      dataIndex: 'dateOperation',
      render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
    },
    {
      title: 'Qte',
      dataIndex: 'qteRestant',
      render: (cell: any, row: OperationEntity) => `${formatNum(cell)}`,
    },
    {
      title: 'Coupon',
      dataIndex: 'tauxInteret',
    },
    {
      title: 'Décote',
      dataIndex: 'decote',
    },
    {
      title: 'Montant',
      render: (cell: any, row: OperationEntity) =>
        `${formatNum(row.prixNominale * 1)}`,
    },
    {
      title: 'Prix total',
      render: (cell: any, row: OperationEntity) =>
        `${formatNum(row.qteRestant * row.prixNominale)}`,
    },
    {
      title: 'Net',
      render: (cell: any, row: OperationEntity) =>
        `${formatNum(
          Math.ceil(
            Number(row.qteRestant) * Number(row.prixNominale) +
              Number(row.couponCouruPayer) +
              Number(row.commIntermediation) +
              Number(row.commConservation) +
              Number(row.commDroitGarde) +
              Number(row.commBrokerage),
          ),
        )}`,
    },
    {
      title: 'Statut',
      dataIndex: 'statut',
      render: (cell: string) => <span className='status-pill'>{cell}</span>,
    },
    {
      title: 'Action',
      render: (cell: any, row: OperationEntity) => (
        <Space>
          <Button
            className='action-button'
            icon={
              <FaPen style={{ color: 'var(--mt-text)', marginBottom: -2 }} />
            }
            style={{
              backgroundColor: 'transparent',
              zoom: 0.8,
            }}
            onClick={() => {
              if (!row.titre) {
                notification.warning({
                  message: 'Titre indisponible',
                  description:
                    "Cet ordre n'a pas de titre rattaché et ne peut pas être modifié depuis le tableau.",
                });
                return;
              }
              setSelectedTitre(row.titre);
              getSelectedTitre(row.titre);
              setSelectedOPE(row);
              setShowModifModal(true);
            }}
          />
          <Button
            className='action-button'
            icon={
              <FaTrash
                style={{ color: 'var(--mt-text)', marginBottom: -2 }}
              />
            }
            style={{
              backgroundColor: 'transparent',
              zoom: 0.8,
            }}
            onClick={() => {
              if (row.titre) {
                setSelectedTitre(row.titre);
                getSelectedTitre(row.titre);
              }
              setSelectedOPE(row);
              setShowDeleteModal(true);
            }}
          />
        </Space>
      ),
    },
  ];

  const visibleOrders = orders.slice(0, MAX_VISIBLE_ACTIVITY_ROWS);
  const hiddenOrders = Math.max(orders.length - visibleOrders.length, 0);

  return (
    <Container>
      {orders.length === 0 ? (
        <div className='empty-state'>Aucun ordre transmis</div>
      ) : (
        <>
          <div className='activity-summary'>
            <strong>Derniers ordres</strong>
            <span>
              {visibleOrders.length}/{orders.length} affichés
            </span>
          </div>
          <Table
            columns={COLUMNS}
            dataSource={visibleOrders}
            rowClassName={(row, index) =>
              index % 2 === 0 ? 'even-row' : 'odd-row'
            }
            rowKey={(row: OperationEntity) => row.id}
            size='small'
            scroll={{ x: 1080 }}
            pagination={false}
          />
          {hiddenOrders > 0 && (
            <div
              className='activity-summary'
              style={{ marginTop: 8, marginBottom: 0 }}
            >
              <span>{hiddenOrders} ordres masqués</span>
              <strong>Vue complète disponible dans l'historique</strong>
            </div>
          )}
        </>
      )}

      <Modal
        className='dark-modal'
        open={showDeleteModal}
        title="Supprimer l'Ordre"
        onCancel={() => setShowDeleteModal(false)}
        footer={[
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              danger
              onClick={() => setShowDeleteModal(false)}
              style={{ border: '2px solid red', borderRadius: 20 }}
            >
              Non
            </Button>
            <Button
              className='butt'
              loading={isLoading}
              style={{ borderRadius: 20 }}
              onClick={deleteOffer}
            >
              Oui
            </Button>
          </div>,
        ]}
      >
        <p>Voulez-vous supprimer cette ordre ?</p>
      </Modal>

      <Modal
        className='dark-modal'
        open={showModifModal}
        title="Modifier l'Ordre"
        onCancel={() => setShowModifModal(false)}
        footer={[
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              danger
              onClick={() => setShowModifModal(false)}
              style={{ border: '2px solid red', borderRadius: 20 }}
            >
              Fermer
            </Button>
            <Button
              className='butt'
              loading={isLoading}
              style={{ borderRadius: 20 }}
              onClick={modifOffer}
            >
              Valider
            </Button>
          </div>,
        ]}
      >
        <Space>
          <div>
            <p style={{ marginBottom: 10 }}>Prix d'achat : </p>
            <p style={{ margin: 0 }}>Quantité : </p>
          </div>
          <div>
            <Select
              size='small'
              value={operation.prix}
              style={{ width: '100%', marginBottom: 15 }}
              options={lmtOptions}
              onChange={(val) =>
                setOperation({
                  ...operation,
                  prix: Number(val),
                  decote: 100 - val / 100,
                })
              }
            />

            <Input
              className='qteInput'
              placeholder='qte'
              value={operation.qte}
              type='number'
              min={0}
              onChange={(e) =>
                setOperation({
                  ...operation,
                  qte: Number(e.target.value),
                })
              }
            />
          </div>
        </Space>
      </Modal>
    </Container>
  );
};
